import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import LazyLoad from 'react-lazyload';
import { HeadTags, NavBarContentData } from '@/shared/types/home.types';
import { api, site } from '@/services/api';
import { FeaturedNewsApi } from '@/pages/ultimas';
import { NewsDataType } from '@/pages/[categorias]/[...news]';
import { BreadcrumbItem } from '@/components/Layout/Breadcrumb';
import { SchemaType } from '@/components/Internals/InternalSchema';
import useBreakpoint from '@/hooks/useBreakpoint';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import Layout from '@/components/Layout';

const NewsItem = dynamic(() => import('@/components/Internals/NewsItem'));

interface PodcastDataType extends NewsDataType {
  podcast: {
    id: string;
    subtitulo: string;
    duracao: string;
    playlist: string;
    publieditorial: string;
    urlOmny: string;
    idOmny: string;
  };
}

interface podcastsProps {
  navBarData: any;
  responseData: {
    data: PodcastDataType;
    segmentacoes: [];
    breadcrumb: BreadcrumbItem[];
    ssr: HeadTags[];
    paginacao?: string;
    schema?: SchemaType;
  };
}

export default function Podcasts({ navBarData, responseData }: podcastsProps): JSX.Element {
  const AdMgid = dynamic(() => import('@/components/Ads/AdMgid'), { ssr: false });

  const { data, ssr, schema } = responseData;
  const { segmentacoes } = data;
  const [isTabletOrMobile] = useBreakpoint('tablet');

  const dataLayer = {
    category: data?.categoriaPrincipal || null,
    author: data?.publicacao?.autor?.nome || null,
    editor: data?.publicacao?.editor?.nome || null,
    contentType: data?.tipo || null,
  };

  return (
    <Layout
      layoutData={{
        segmentacoes,
        tagsData: ssr,
        navBarData,
        schema,
        contentType: data.tipo,
        breadcrumbList: data.breadcrumb,
      }}
      dataLayer={dataLayer}>
      <NewsItem newsData={data} isCurrentPage type="podcast" />
      <LazyLoad height={1}>
        <AdMgid type={isTabletOrMobile ? 'mobile' : 'desktop'} />
      </LazyLoad>
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { playlists, podcasts = [] } = context.query;
  const { host } = context.req.headers;

  const mainReq = { promise: site.get<FeaturedNewsApi>(`/podcast/${playlists}/${podcasts}/`), link: `/podcast/${playlists}/${podcasts}/` };
  const responseMenuReq = { promise: api.get<NavBarContentData>(`/componentes/menu/`), link: `/componentes/menu/` };

  const { promiseData, data, statusCode } = await getConcurrentPromises({
    mainPromise: mainReq,
    navBarPromise: responseMenuReq,
    host,
  });

  if (data.redirect || data.notFound) return data;

  const responseData = data;
  const navBarData: NavBarContentData = promiseData[responseMenuReq.link].data.data;

  context.res.setHeader('Cache-Control', data.headers['Cache-control']);
  context.res.setHeader('Expires', data.headers.Expires);
  context.res.statusCode = statusCode;

  return {
    props: {
      responseData,
      navBarData,
    },
  };
};
